import { motion } from 'framer-motion'

export type StepStatus = 'pending' | 'active' | 'completed'

export interface Step {
  id: string
  label: string
  icon: React.ReactNode
  status: StepStatus
}

export interface ProgressStepsProps {
  steps: Step[]
}

const ProgressSteps = ({ steps }: ProgressStepsProps) => {
  return (
    <div className="w-full">
      <div className="grid grid-cols-2 sm:grid-cols-4 items-center w-full">
        {steps.map((step, index) => {
          return (
            <div key={step.id} className="relative flex flex-col items-center">
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <motion.div
                  initial={false}
                  animate={{
                    scale: step.status === 'active' ? 1.1 : 1,
                    backgroundColor:
                      step.status === 'completed'
                        ? 'rgb(16, 185, 129)'
                        : step.status === 'active'
                          ? 'rgb(59, 130, 246)'
                          : 'rgb(51, 65, 85)',
                  }}
                  transition={{ duration: 0.3 }}
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center
                    border-2
                    ${step.status === 'completed'
                      ? 'border-green-500'
                      : step.status === 'active'
                        ? 'border-primary-500'
                        : 'border-dark-700'
                    }
                  `}
                >
                  {step.status === 'completed' ? (
                    <motion.svg
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </motion.svg>
                  ) : (
                    <div
                      className={`
                        ${step.status === 'active' ? 'text-white' : 'text-dark-400'}
                      `}
                    >
                      {step.icon}
                    </div>
                  )}
                </motion.div>

                <p
                  className={`
                    mt-2 text-xs font-medium text-center
                    ${step.status === 'completed'
                      ? 'text-green-400'
                      : step.status === 'active'
                        ? 'text-primary-400'
                        : 'text-dark-500'
                    }
                  `}
                >
                  {step.label}
                </p>
              </div>

              {/* Connector line */}
              {index !== steps.length - 1 && (
                <>
                  {/* Mobile (2 cols): only draw for left column items */}
                  {index % 2 === 0 && (
                    <div className="absolute top-6 left-1/2 w-full px-6 sm:hidden">
                      <div className="h-0.5 bg-dark-700 w-full" />
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: step.status === 'completed' ? 1 : 0 }}
                        transition={{ duration: 0.5 }}
                        className="h-0.5 bg-green-500 origin-left w-full -mt-0.5"
                      />
                    </div>
                  )}

                  {/* sm+ (4 cols): draw for all except last col */}
                  {index % 4 !== 3 && (
                    <div className="absolute top-6 left-1/2 w-full px-6 hidden sm:block">
                      <div className="h-0.5 bg-dark-700 w-full" />
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: step.status === 'completed' ? 1 : 0 }}
                        transition={{ duration: 0.5 }}
                        className="h-0.5 bg-green-500 origin-left w-full -mt-0.5"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ProgressSteps
